import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n, LANGUAGES } from '../contexts/I18nContext';
import { Globe } from 'lucide-react';

/**
 * Floating language switcher — homepage only.
 * Anchored inside the hero area (not fixed to page edges).
 */
export default function LanguagePill() {
  const { language, setLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const flag = LANGUAGES[language]?.flag || '🌐';
  const name = LANGUAGES[language]?.name || 'English';
  const location = useLocation();
  // Only render on the homepage — never overlay other routes' tabs/nav.
  if (location.pathname !== '/') return null;

  return (
    <div className="fixed top-20 right-6 z-40" data-i18n-skip data-testid="language-pill">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0A0B]/90 backdrop-blur border border-[#1E1E24] text-xs text-zinc-200 hover:border-[#06B6D4] hover:text-white transition-colors shadow-lg"
        aria-label="Change language"
        title="Change language"
        data-testid="language-pill-btn"
      >
        <Globe className="w-3.5 h-3.5 text-[#06B6D4]" />
        <span className="text-base leading-none">{flag}</span>
        <span className="font-mono uppercase tracking-wider text-[10px]">{language}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-0" onClick={() => setOpen(false)} role="presentation" />
          <div
            className="absolute right-0 top-full mt-2 w-64 max-h-80 overflow-y-auto bg-[#0A0A0B] border border-[#1E1E24] rounded-xl shadow-xl p-1"
            data-testid="language-pill-menu"
          >
            {Object.entries(LANGUAGES).map(([code, meta]) => (
              <button
                key={code}
                onClick={() => { setLanguage(code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm
                  ${code === language ? 'bg-[#06B6D4]/15 text-white' : 'text-zinc-300 hover:bg-white/5'}`}
                data-testid={`language-option-${code}`}
              >
                <span className="text-lg leading-none">{meta.flag}</span>
                <div className="flex-1">
                  <div>{meta.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase">{code}</div>
                </div>
                {code === language && <span className="text-[#06B6D4] text-xs">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
